import { makeAutoObservable, runInAction } from 'mobx';

/**
 * What the popup-side handler asks the user to consent to. Two shapes:
 * delegation (Spec §3) and extrinsic/byte signing.
 *
 * `kind` is a required discriminant on both variants so `ConsentRequest` is a
 * proper discriminated union — `request.kind === 'sign'` narrows in both
 * directions, and a delegation request can never be confused with a sign
 * request that simply forgot its tag.
 */
export interface DelegationConsentRequest {
  kind: 'delegation';
  hostOrigin: string;
  capabilities: string[];
  ttlSeconds: number;
  agentPubkey?: string;
}

export interface SignConsentRequest {
  kind: 'sign';
  hostOrigin: string;
  /** The `wallet:sign` requestKind being consented to, e.g. 'extrinsic'. */
  signKind: string;
  /** Best-effort human description supplied by the caller (may be absent). */
  humanReadable?: string;
  /** Short digest of the payload being signed, shown for identification. */
  payloadDigest: string;
}

export type ConsentRequest = DelegationConsentRequest | SignConsentRequest;

export type ConsentDecision = { kind: 'approve-once' } | { kind: 'approve-always' } | { kind: 'deny' };

export interface PendingConsent {
  request: ConsentRequest;
  resolve: (d: ConsentDecision) => void;
}

/**
 * Holds at most one in-flight delegation-consent prompt. The popup-side
 * `createRequestDelegationHandler` calls `request()` and awaits the promise;
 * the React `<ConsentPrompt>` observer renders the modal when `pending !== null`
 * and calls `resolveCurrent()` when the user clicks a button.
 */
export class PendingConsentStore {
  pending: PendingConsent | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  request(req: ConsentRequest): Promise<ConsentDecision> {
    if (this.pending) {
      throw new Error('PendingConsentStore: a consent prompt is already in flight');
    }
    return new Promise<ConsentDecision>((resolve) => {
      runInAction(() => {
        this.pending = { request: req, resolve };
      });
    });
  }

  resolveCurrent(decision: ConsentDecision): void {
    const current = this.pending;
    if (!current) return;
    runInAction(() => {
      this.pending = null;
    });
    current.resolve(decision);
  }
}
